import { WORK_ORDER_DISPLAY_STATUSES } from '../constants/WorkOrderDisplayStatuses';
import { PENDING_APPROVAL_STATUSES, WORK_ORDER_STATUSES } from '../constants/WorkOrderStatuses';
import {
	getBuyerCompanySettingValue,
	isBuyerOrInternalTech,
	isInternalTech,
	isThirdPartySupplier,
} from './AuthUtils';
import {
	isContractor,
	isSupplierPayingCustomerAndAdmin,
	nullSafeGet,
	nullSafeGetOrElse,
	nullSafeGetWithZeroOrElse,
	recordHasContractorId,
} from './DataAccessUtils';
import { CONTRACTOR_PROPS, FILTER_CONFIG_NAMES, ROLE_TYPES } from './DataConstants';
import moment from 'moment';
import { getLinkWIthBackLinkParams } from './HistoryUtils';
import WalkthroughTaskListAnswerDisplay from '../components/walkthroughs_detail_details_page/WalkthroughTaskListAnswerDisplay';

export const UNREAD_NOTES_NOTIFICATION_KEY = 'hasUnreadNotesNotifications';

/*
    1. Is internal supplier? => null
    2. Is asset warranty? => asset warranty nte
    3. Has existing NTE? => Existing NTE
    4. Has work order attributes NTE? => Work Order Attribute NTE
    5. Has no NTE? => undefined
 */
export const getNewLocationNTE = (
	hasSupplier,
	privateSupplier,
	assetWarrantyNTE,
	locationNTE,
	workOrderAttributesNTE,
	currencyExchangeRateBuyerToLocation
) =>
	!hasSupplier || privateSupplier
		? null //1. Is internal supplier? => null
		: assetWarrantyNTE
		? assetWarrantyNTE //2. Is asset warranty? => asset warranty nte
		: locationNTE
		? locationNTE // 3. Has existing NTE? => Existing NTE
		: workOrderAttributesNTE
		? workOrderAttributesNTE * currencyExchangeRateBuyerToLocation //4. Has work order attributes NTE? => Work Order Attribute NTE
		: 0; //5. Has no NTE? => 0

export const BUYER_WO_FILTERS_ORDER = [
	FILTER_CONFIG_NAMES.LOCATION,
	FILTER_CONFIG_NAMES.AREA,
	FILTER_CONFIG_NAMES.PRIORITY,
	FILTER_CONFIG_NAMES.ASSET,
	FILTER_CONFIG_NAMES.ASSET_TYPE,
	FILTER_CONFIG_NAMES.PROBLEM_TYPE,
	FILTER_CONFIG_NAMES.SUPPLIER,
	FILTER_CONFIG_NAMES.BRAND,
	FILTER_CONFIG_NAMES.CREATED_BY,
	FILTER_CONFIG_NAMES.NOTES_STATUS,
	FILTER_CONFIG_NAMES.RESOULTION_TYPE,
	FILTER_CONFIG_NAMES.CREATED_AT,
	FILTER_CONFIG_NAMES.COMPLETED_AT,
	FILTER_CONFIG_NAMES.LAST_SERVICE_CALL_SCHEDULED_AT,
	FILTER_CONFIG_NAMES.IS_PAST_DUE,
	FILTER_CONFIG_NAMES.IS_PAST_SCHEDULE,
	FILTER_CONFIG_NAMES.IS_PAST_ESTIMATED_COMPLETION,
	FILTER_CONFIG_NAMES.IS_CAPEX,
	FILTER_CONFIG_NAMES.IS_STALE,
	FILTER_CONFIG_NAMES.IS_PLANNED_MAINTENANCE,
	FILTER_CONFIG_NAMES.IS_WARRANTY,
	FILTER_CONFIG_NAMES.IS_ASSET_DOWN,
	FILTER_CONFIG_NAMES.PM_SCHEDULE,
	FILTER_CONFIG_NAMES.SITE_SURVEY_TASK,
];

export const INTERNAL_SERVICE_TEAM_WO_FILTERS_ORDER = [
	FILTER_CONFIG_NAMES.LOCATION,
	FILTER_CONFIG_NAMES.AREA,
	FILTER_CONFIG_NAMES.TECHNICIAN,
	FILTER_CONFIG_NAMES.SUPPLIER,
	FILTER_CONFIG_NAMES.PRIORITY,
	FILTER_CONFIG_NAMES.ASSET,
	FILTER_CONFIG_NAMES.ASSET_TYPE,
	FILTER_CONFIG_NAMES.PROBLEM_TYPE,
	FILTER_CONFIG_NAMES.CREATED_BY,
	FILTER_CONFIG_NAMES.NOTES_STATUS,
	FILTER_CONFIG_NAMES.RESOULTION_TYPE,
	FILTER_CONFIG_NAMES.CREATED_AT,
	FILTER_CONFIG_NAMES.COMPLETED_AT,
	FILTER_CONFIG_NAMES.LAST_SERVICE_CALL_SCHEDULED_AT,
	FILTER_CONFIG_NAMES.IS_PAST_DUE,
	FILTER_CONFIG_NAMES.IS_PAST_SCHEDULE,
	FILTER_CONFIG_NAMES.IS_PAST_ESTIMATED_COMPLETION,
	FILTER_CONFIG_NAMES.IS_CAPEX,
	FILTER_CONFIG_NAMES.IS_STALE,
	FILTER_CONFIG_NAMES.IS_PLANNED_MAINTENANCE,
	FILTER_CONFIG_NAMES.IS_WARRANTY,
	FILTER_CONFIG_NAMES.IS_ASSET_DOWN,
	FILTER_CONFIG_NAMES.PM_SCHEDULE,
	FILTER_CONFIG_NAMES.SITE_SURVEY_TASK,
];

export const THIRD_PARTY_SUPPLIER_WO_FILTERS_ORDER = [
	FILTER_CONFIG_NAMES.LOCATION,
	FILTER_CONFIG_NAMES.TECHNICIAN,
	FILTER_CONFIG_NAMES.SUB_CONTRACTORS,
	FILTER_CONFIG_NAMES.PRIORITY,
	FILTER_CONFIG_NAMES.TRADE,
	FILTER_CONFIG_NAMES.CREATED_BY,
	FILTER_CONFIG_NAMES.NOTES_STATUS,
	FILTER_CONFIG_NAMES.CREATED_AT,
	FILTER_CONFIG_NAMES.COMPLETED_AT,
	FILTER_CONFIG_NAMES.LAST_SERVICE_CALL_SCHEDULED_AT,
	FILTER_CONFIG_NAMES.IS_STALE,
	FILTER_CONFIG_NAMES.IS_PLANNED_MAINTENANCE,
	FILTER_CONFIG_NAMES.IS_WARRANTY,
	FILTER_CONFIG_NAMES.SITE_SURVEY_TASK,
];

export const PAYING_SUPPLIER_WO_FILTER_ORDER = [
	FILTER_CONFIG_NAMES.CONTRACTOR_CLIENT,
	FILTER_CONFIG_NAMES.CONTRACTOR_LOCATION,
	FILTER_CONFIG_NAMES.TECHNICIAN,
	FILTER_CONFIG_NAMES.SUB_CONTRACTORS,
	FILTER_CONFIG_NAMES.PRIORITY,
	FILTER_CONFIG_NAMES.TRADE,
	FILTER_CONFIG_NAMES.CREATED_BY,
	FILTER_CONFIG_NAMES.NOTES_STATUS,
	FILTER_CONFIG_NAMES.CREATED_AT,
	FILTER_CONFIG_NAMES.COMPLETED_AT,
	FILTER_CONFIG_NAMES.LAST_SERVICE_CALL_SCHEDULED_AT,
	FILTER_CONFIG_NAMES.IS_STALE,
	FILTER_CONFIG_NAMES.IS_PLANNED_MAINTENANCE,
	FILTER_CONFIG_NAMES.IS_WARRANTY,
];

export const getAssetFilterForWOCreation = (userType) => (companyConfig, currentUser, state) => {
	const areaAllowed = getBuyerCompanySettingValue(userType)(
		'assetConfig.allowAreaAssociationWithAssets',
		companyConfig,
		currentUser,
		false
	);

	return {
		locationId: nullSafeGet('location.id', state),
		problemTypeId: nullSafeGet('problemType.id', state),
		...(areaAllowed &&
			nullSafeGet('area.id', state) && {
				areaIdOrNull: nullSafeGet('area.id', state),
			}),
	};
};

export const getSupplierWorkOrderServiceCalls = (workOrder, currentUser, isClientView) => {
	const sanitisedWO = getSanitisedWorkOrder(workOrder, isClientView);
	const allServiceCalls = nullSafeGetOrElse('associatedServiceCalls', sanitisedWO, []);
	// if internalTech show all service calls
	if (isInternalTech(currentUser)) {
		return allServiceCalls;
	}
	return allServiceCalls.filter(
		(_) =>
			_.supplierFacilityId === sanitisedWO.supplierFacilityId ||
			_.supplierFacilityId === sanitisedWO.subContractorSupplierFacilityId
	);
};

export const isAllowedToEditTasksByWOStatus = (workOrder, currentUser) => {
	const allowedByWOStatus =
		workOrder.displayStatus !== WORK_ORDER_DISPLAY_STATUSES.complete &&
		workOrder.displayStatus !== WORK_ORDER_DISPLAY_STATUSES.cancelled &&
		workOrder.status !== WORK_ORDER_STATUSES.waitingForReview;

	const isPrivateSupplier =
		nullSafeGet('facility.status', currentUser) === 'private' && allowedByWOStatus;
	const allowedForPayingCustomer =
		nullSafeGet('facility.id', currentUser) !== workOrder.supplierFacilityId &&
		nullSafeGet('isSupplierInitiated', workOrder) &&
		isSupplierPayingCustomerAndAdmin(currentUser) &&
		allowedByWOStatus;

	const allowedForThirdPartyBasedOnStatus =
		!isPrivateSupplier && workOrder.displayStatus === WORK_ORDER_DISPLAY_STATUSES.pending;

	if (currentUser.userType === ROLE_TYPES.SUPPLIER) {
		return isPrivateSupplier || allowedForPayingCustomer || allowedForThirdPartyBasedOnStatus;
	} else {
		return allowedByWOStatus;
	}
};

export const isWOTaskCheckFailed = (workOrder, tasks = []) =>
	nullSafeGetOrElse('taskCompletionMandatory', workOrder, false) &&
	!!tasks.find((_) => nullSafeGetOrElse('answer', _, 'false') !== 'true');

export const getFieldsSetForWorkOrderDefaults = (location, submittedByNameIsRequired) => {
	const urlParams = new URLSearchParams(location.search);
	const locationId = urlParams.get('locationId');
	const areasTopLevel = urlParams.get('areasTopLevel');
	const areaId = urlParams.get('areaId');
	const areaIdNextLevel = urlParams.get('areaIdNextLevel');
	const assetId = urlParams.get('assetId');
	const problemTypeId = urlParams.get('problemTypeId');
	const problemTypeTopLevel = urlParams.get('problemTypeTopLevel');
	const problemTypeIdNextLevel = urlParams.get('problemTypeIdNextLevel');
	const isAssetDown = urlParams.get('isAssetDown');
	const supplierFacilityId = urlParams.get('supplierFacilityId');
	const submittedByName = urlParams.get('submittedByName');
	const defaultPriorityId = urlParams.get('defaultPriorityId');
	const walkthroughId = urlParams.get('walkthroughId');
	const siteSurveyTaskTitleId = urlParams.get('siteSurveyTaskTitleId');

	const fieldsToSet: any = {};
	if (locationId) fieldsToSet.locationId = locationId;
	if (areaId) fieldsToSet.areaId = areaId;
	if (assetId) fieldsToSet.assetId = assetId;
	if (areasTopLevel) fieldsToSet.areasTopLevel = areasTopLevel;
	if (areaIdNextLevel) fieldsToSet.areaIdNextLevel = areaIdNextLevel;
	if (problemTypeTopLevel) fieldsToSet.problemTypeTopLevel = problemTypeTopLevel;
	if (problemTypeId) fieldsToSet.problemTypeId = problemTypeId;
	if (problemTypeIdNextLevel) fieldsToSet.problemTypeIdNextLevel = problemTypeIdNextLevel;
	if (isAssetDown) fieldsToSet.isAssetDown = isAssetDown;
	if (supplierFacilityId) fieldsToSet.supplierFacilityId = supplierFacilityId;
	if (submittedByName && submittedByNameIsRequired) fieldsToSet.submittedByName = submittedByName;
	if (defaultPriorityId) fieldsToSet.woPriorityId = defaultPriorityId;
	if (walkthroughId) fieldsToSet.walkthroughId = walkthroughId;
	if (siteSurveyTaskTitleId) fieldsToSet.siteSurveyTaskTitleId = siteSurveyTaskTitleId;
	return fieldsToSet;
};

export const getApprovedOrDeclinedDetail = (workOrder) => {
	const approvedOrDeniedByContact = nullSafeGet('approvedOrDeniedByContact', workOrder);
	const actionName =
		nullSafeGet('displayStatus', workOrder) !== WORK_ORDER_DISPLAY_STATUSES.cancelled &&
		!PENDING_APPROVAL_STATUSES.includes(nullSafeGet('status', workOrder))
			? 'Approved'
			: null;

	return approvedOrDeniedByContact && actionName
		? {
				keyName: `${actionName} by`,
				contactObject: approvedOrDeniedByContact,
		  }
		: null;
};

export const isDifferentAssignedSupplier = (workOrder, currentUser) => {
	const facilityId = nullSafeGet('facility.id', currentUser);
	return (
		facilityId &&
		facilityId !== workOrder.supplierFacilityId &&
		facilityId !== nullSafeGet('subContractedFromWorkOrder.subSupplierFacilityId', workOrder)
	);
};

export const isDifferentAssignedSupplierBasedOnView = (workOrder, currentUser, isClientView) => {
	const facilityId = nullSafeGet('facility.id', currentUser);
	return (
		facilityId &&
		(isClientView
			? facilityId !== workOrder.supplierFacilityId &&
			  facilityId !== nullSafeGet('subContractedFromWorkOrder.subSupplierFacilityId', workOrder)
			: true) //If it is subcontractor view, it is definitely assigned for a different supplier
	);
};

export const isManagedBuyerCompanyIdWO = (workOrder, currentUser) => {
	const supplierCompanyId = nullSafeGet('facility.supplierCompanyId', currentUser);
	return (
		supplierCompanyId &&
		supplierCompanyId === nullSafeGet('buyerCompany.managedBySupplierCompanyId', workOrder)
	);
};

export const biddingOnWorkOrderAssociatedWithAnotherSupplier = (workOrder, currentUser) => {
	const isThirdPartySupplier =
		currentUser.userType === ROLE_TYPES.SUPPLIER && !isInternalTech(currentUser);

	return isThirdPartySupplier && isDifferentAssignedSupplier(workOrder, currentUser);
};

export const isSubContractedWorkOrder = (workOrder) =>
	!!nullSafeGet('subContractedToWorkOrder.subSupplierFacilityId', workOrder);

export const isSubContractedFromWorkOrder = (workOrder) =>
	!!nullSafeGet('subContractedFromWorkOrder.subSupplierFacilityId', workOrder);

const getSubcontractedValues = (
	entity,
	woPropName,
	contractorWOPropName,
	isClientView,
	lookForToWOAsWell = false
) => {
	const getValue = (woEntityName) =>
		nullSafeGetWithZeroOrElse(
			`${woEntityName}.${contractorWOPropName}`,
			entity,
			entity[woPropName]
		);

	const clientValue = getValue('subContractedFromWorkOrder');
	const toWOValue = getValue('subContractedToWorkOrder');

	return isClientView
		? clientValue
			? clientValue
			: lookForToWOAsWell
			? toWOValue
			: clientValue
		: toWOValue;
};

const getWOPropValuesFromMayBeClient = (
	workOrder,
	contractorPropName,
	woPropName,
	isClientView
) => {
	const clientValue = nullSafeGet(`subContractedFromWorkOrder.${contractorPropName}`, workOrder);
	const toWOValue = nullSafeGet(`subContractedToWorkOrder.${contractorPropName}`, workOrder);
	const woValue = workOrder[woPropName];

	return workOrder.locationId === -100
		? isClientView
			? clientValue || toWOValue
			: toWOValue
		: isClientView
		? clientValue || woValue || toWOValue
		: toWOValue || woValue;
};

export const getSanitisedWorkOrder = (workOrder, isClientView = true) => ({
	...workOrder,
	originalWorkOrder: isSubContractedWorkOrder(workOrder) ? workOrder : undefined,
	title: getSubcontractedValues(workOrder, 'title', 'updatedTitle', isClientView),
	description: getSubcontractedValues(workOrder, 'description', 'updatedDescription', isClientView),
	nte: getSubcontractedValues(workOrder, 'nte', 'updatedNte', isClientView),
	status: getSubcontractedValues(workOrder, 'status', 'contractorStatus', isClientView),
	estimatedCompletionDate: getSubcontractedValues(
		workOrder,
		'estimatedCompletionDate',
		'subEstimatedCompletionDate',
		isClientView
	),
	displayStatus: getSubcontractedValues(
		workOrder,
		'displayStatus',
		'contractorDisplayStatus',
		isClientView
	),
	supplierAttachments: isClientView
		? nullSafeGet('subContractedFromWorkOrder', workOrder)
			? nullSafeGetOrElse('subContractedFromWorkOrder.subContractorAttachments', workOrder, [])
			: nullSafeGetOrElse('supplierAttachments', workOrder, [])
		: nullSafeGetOrElse('subContractedToWorkOrder.contractorAttachments', workOrder, []),
	subContractorAttachments: isClientView
		? []
		: nullSafeGetOrElse('subContractedToWorkOrder.subContractorAttachments', workOrder, []),

	contractorAttachments: isClientView
		? nullSafeGetOrElse('subContractedFromWorkOrder.contractorAttachments', workOrder, [])
		: [],
	location: getWOPropValuesFromMayBeClient(
		workOrder,
		'contractorLocation',
		'location',
		isClientView
	),
	problemType: getWOPropValuesFromMayBeClient(
		workOrder,
		'contractorProblemType',
		'problemType',
		isClientView
	),
	woPriority: getWOPropValuesFromMayBeClient(
		workOrder,
		'contractorWorkOrderPriority',
		'woPriority',
		isClientView
	),
	contractorClient: getSubcontractedValues(
		workOrder,
		'contractorClient',
		'contractorClient',
		isClientView,
		true
	),
	associatedServiceCalls: getSubcontractedValues(
		workOrder,
		'associatedServiceCalls',
		'associatedContractorServiceCalls',
		isClientView
	),
	lastServiceCall: getSubcontractedValues(
		workOrder,
		'lastServiceCall',
		'lastContractorServiceCall',
		isClientView
	),
	supplierFacilityId: getSubcontractedValues(
		workOrder,
		'supplierFacilityId',
		'subSupplierFacilityId',
		isClientView
	),
	supplierFacility: getSubcontractedValues(
		workOrder,
		'supplierFacility',
		'subSupplierFacility',
		isClientView
	),
	contractorWo: getContractorWO(workOrder, isClientView),
	buyerSubscribers: getSubcontractedValues(
		workOrder,
		'buyerSubscribers',
		'contractorSubscribers',
		isClientView
	),
	supplierSubscribers: getSubcontractedValues(
		workOrder,
		'supplierSubscribers',
		'subSubscribers',
		isClientView
	),
	hasSupplierArchived: getSubcontractedValues(
		workOrder,
		'hasSupplierArchived',
		'hasSubArchived',
		isClientView
	),
});

export const getWorkOrderfromContractedWorkOrder = (workOrder, isClientView = true) => ({
	...workOrder,
	title: nullSafeGetOrElse('updatedTitle', workOrder, nullSafeGet('title', workOrder)),
	displayStatus: nullSafeGetOrElse(
		'contractorDisplayStatus',
		workOrder,
		nullSafeGet('displayStatus', workOrder)
	),
	description: nullSafeGetOrElse(
		'updatedDescription',
		workOrder,
		nullSafeGet('description', workOrder)
	),
	nte: nullSafeGetOrElse('updatedNte', workOrder, nullSafeGet('nte', workOrder)),
	status: nullSafeGetOrElse('contractorStatus', workOrder, nullSafeGet('status', workOrder)),
	supplierFacilityId: nullSafeGetOrElse(
		'subSupplierFacilityId',
		workOrder,
		nullSafeGet('supplierFacilityId', workOrder)
	),
	supplierFacility: nullSafeGetOrElse(
		'subSupplierFacility',
		workOrder,
		nullSafeGet('supplierFacility', workOrder)
	),
});

export const getContractorWO = (workOrder, isClientView = true) =>
	isClientView
		? nullSafeGet('subContractedFromWorkOrder', workOrder)
		: nullSafeGet('subContractedToWorkOrder', workOrder);

export const applyMarkup = (quote, markupPercentage) => {
	const markupFactor = 1 + markupPercentage / 100;

	function applyMarkupToLineItems(lineItems) {
		return lineItems.map((item) => ({
			...item,
			hourlyRate: (parseFloat(item.hourlyRate) * markupFactor).toFixed(2),
			amount: (parseFloat(item.amount) * markupFactor).toFixed(2),
		}));
	}

	function applyMarkupToMaterials(materialItems) {
		return materialItems.map((item) => ({
			...item,
			unitPrice: (parseFloat(item.unitPrice) * markupFactor).toFixed(2),
			amount: (parseFloat(item.amount) * markupFactor).toFixed(2),
		}));
	}
	function applyMarkupToMisc(miscItems) {
		return miscItems.map((item) => ({
			...item,
			amount: (parseFloat(item.amount) * markupFactor).toFixed(2),
		}));
	}

	return {
		...quote,
		laborTotalBeforeTax: (parseFloat(quote.laborTotalBeforeTax) * markupFactor).toFixed(2),
		laborLineItems: applyMarkupToLineItems(quote.laborLineItems),
		materialTotalBeforeTax: (parseFloat(quote.materialTotalBeforeTax) * markupFactor).toFixed(2),
		materialLineItems: applyMarkupToMaterials(quote.materialLineItems),
		totalBeforeTax: (parseFloat(quote.totalBeforeTax) * markupFactor).toFixed(2),
		totalAfterTax: (parseFloat(quote.totalAfterTax) * markupFactor).toFixed(2),
		tax: (parseFloat(quote.tax) * markupFactor).toFixed(2),
		incurredCostTotalBeforeTax: (parseFloat(quote.tax) * markupFactor).toFixed(2),
		travelTotalBeforeTax: (parseFloat(quote.travelTotalBeforeTax) * markupFactor).toFixed(2),
		freightTotalBeforeTax: (parseFloat(quote.freightTotalBeforeTax) * markupFactor).toFixed(2),
		miscTotalBeforeTax: (parseFloat(quote.miscTotalBeforeTax) * markupFactor).toFixed(2),
		miscLineItems: applyMarkupToMisc(quote.miscLineItems),
	};
};
export const applyMarkupToInvoice = (invoice, markupPercentage) => {
	const markupFactor = 1 + markupPercentage / 100;

	function applyMarkupToLineItems(lineItems) {
		return lineItems.map((item) => ({
			...item,
			hourlyRate: (parseFloat(item.hourlyRate) * markupFactor).toFixed(2),
			amount: (parseFloat(item.amount) * markupFactor).toFixed(2),
		}));
	}

	function applyMarkupToMaterials(materialItems) {
		return materialItems.map((item) => ({
			...item,
			unitPrice: (parseFloat(item.unitPrice) * markupFactor).toFixed(2),
			amount: (parseFloat(item.amount) * markupFactor).toFixed(2),
		}));
	}
	function applyMarkupToMisc(miscItems) {
		return miscItems.map((item) => ({
			...item,
			amount: (parseFloat(item.amount) * markupFactor).toFixed(2),
		}));
	}
	function applyMarkupToTaxItems(miscItems = []) {
		return (miscItems || []).map((item) => ({
			...item,
			amount: (parseFloat(item.amount) * markupFactor).toFixed(2),
		}));
	}

	return {
		...invoice,
		laborTotalBeforeTax: (parseFloat(invoice.laborTotalBeforeTax) * markupFactor).toFixed(2),
		laborLineItems: applyMarkupToLineItems(invoice.laborLineItems),
		materialTotalBeforeTax: (parseFloat(invoice.materialTotalBeforeTax) * markupFactor).toFixed(2),
		materialLineItems: applyMarkupToMaterials(invoice.materialLineItems),
		invoiceTotalBeforeTax: (parseFloat(invoice.invoiceTotalBeforeTax) * markupFactor).toFixed(2),
		invoiceTotalAfterTax: (parseFloat(invoice.invoiceTotalAfterTax) * markupFactor).toFixed(2),
		invoiceTax: (parseFloat(invoice.invoiceTax) * markupFactor).toFixed(2),
		incurredCostTotalBeforeTax: (parseFloat(invoice.tax) * markupFactor).toFixed(2),
		travelTotalBeforeTax: (parseFloat(invoice.travelTotalBeforeTax) * markupFactor).toFixed(2),
		freightTotalBeforeTax: (parseFloat(invoice.freightTotalBeforeTax) * markupFactor).toFixed(2),
		miscTotalBeforeTax: (parseFloat(invoice.miscTotalBeforeTax) * markupFactor).toFixed(2),
		miscLineItems: applyMarkupToMisc(invoice.miscLineItems),
		taxLineItems: applyMarkupToTaxItems(invoice.taxLineItems),
	};
};

export const getSanitisedQuote = (quote, isClientView = true) => {
	return {
		...quote,
		supplierFacilityId: nullSafeGetOrElse(
			'supplierFacilityId',
			quote,
			nullSafeGet('subSupplierFacilityId', quote)
		),
		supplierFacility: nullSafeGetOrElse(
			'supplierFacility',
			quote,
			nullSafeGet('subSupplierFacility', quote)
		),
		workOrder: getSanitisedWorkOrder(quote.workOrder, isClientView),
	};
};
export const getSanitisedInvoice = (invoice, isClientView = true) => ({
	...invoice,
	supplierFacilityId: nullSafeGetOrElse(
		'supplierFacilityId',
		invoice,
		nullSafeGet('subSupplierFacilityId', invoice)
	),
	supplierFacility: nullSafeGetOrElse(
		'supplierFacility',
		invoice,
		nullSafeGet('subSupplierFacility', invoice)
	),
	workOrder: getWorkOrderfromContractedWorkOrder(invoice.workOrder || invoice.contractorWorkOrder),
});

export const getContractorOrWorkOrderId = (workOrder) => {
	return nullSafeGet('contractorWo.id', workOrder) || nullSafeGet('id', workOrder);
};

export const getBuyerOrContractorOrClientDetailsFromSearch = (workOrder) => {
	const subContractedDetails = nullSafeGetOrElse('subContractedDetails', workOrder, []);
	const supplierFacilityId = workOrder.currentUserSupplierFacilityId;

	const subContractedFromDetailsFromSearch = subContractedDetails.find(
		(_) => _.subContractedToSupplierFacilityId === supplierFacilityId
	);
	const nameFromFromWOSearch = nullSafeGet(
		'subContractedFromSupplierFacilityName',
		subContractedFromDetailsFromSearch
	);
	const fromWOObj = nameFromFromWOSearch
		? {
				key: CONTRACTOR_PROPS.FROM_WO,
				value: {
					contractorSupplierFacility: {
						displayName: nameFromFromWOSearch,
					},
				},
		  }
		: null;

	const toWODetailFromSearch = subContractedDetails.find(
		(_) => _.subContractedFromSupplierFacilityId === supplierFacilityId
	);
	const contractorClientNameFromSearch = nullSafeGet('contractorClientName', toWODetailFromSearch);
	const toWOObj = contractorClientNameFromSearch
		? {
				key: CONTRACTOR_PROPS.TO_WO,
				value: {
					contractorClient: {
						name: contractorClientNameFromSearch,
					},
				},
		  }
		: null;

	const buyerCompanyName = nullSafeGet('buyerCompanyName', workOrder);
	const buyerCompanyObj = buyerCompanyName
		? {
				key: 'buyerCompany',
				value: {
					name: buyerCompanyName,
				},
		  }
		: null;

	return fromWOObj || buyerCompanyObj || toWOObj;
};

export const getBuyerOrContractorNameWithHighlightKey = (workOrder, currentUser) => {
	const thirdPartySupplier = isThirdPartySupplier(currentUser);
	const fromWOContractorSupplierFacilityName = nullSafeGet(
		`${CONTRACTOR_PROPS.FROM_WO}.contractorSupplierFacility.displayName`,
		workOrder
	);

	const toWOClientName = nullSafeGet(`${CONTRACTOR_PROPS.TO_WO}.contractorClient.name`, workOrder);

	const buyerCompanyName = nullSafeGet('buyerCompany.name', workOrder);

	const supplierFacilityName = nullSafeGet('supplierFacility.displayName', workOrder);

	if (thirdPartySupplier) {
		if (fromWOContractorSupplierFacilityName) {
			return {
				key: 'subContractedDetails.subContractedFromSupplierFacilityName',
				value: fromWOContractorSupplierFacilityName,
			};
		}
		if (buyerCompanyName) {
			return {
				key: 'buyerCompanyName',
				value: buyerCompanyName,
			};
		}
		if (toWOClientName) {
			return {
				key: 'subContractedDetails.contractorClientName',
				value: toWOClientName,
			};
		}
		return undefined;
	}

	if (supplierFacilityName) {
		return {
			key: 'supplierFacilityDisplayName',
			value: supplierFacilityName,
		};
	}

	return undefined;
};

export const isSelfClientWorkOrder = (workOrder, currentUser) => {
	const supplierFacilityId = nullSafeGet('facility.id', currentUser);
	const clientSupplierFacilityId = nullSafeGet(
		`${CONTRACTOR_PROPS.TO_WO}.contractorClient.clientSupplierFacilityId`,
		workOrder
	);
	return (
		supplierFacilityId &&
		clientSupplierFacilityId &&
		clientSupplierFacilityId === supplierFacilityId
	);
};

export const getWorkOrderPath = (userType, currentUser, record) => {
	const hasContractorId = recordHasContractorId(record);
	const isClientView = !isContractor(currentUser, record);
	return userType === ROLE_TYPES.SUPPLIER
		? hasContractorId
			? isClientView
				? ''
				: 'subcontractor_'
			: ''
		: '';
};

export const getWorkOrderArchivalKey = (currentUser: any) => {
	return isBuyerOrInternalTech(currentUser) ? 'hasBuyerArchived' : 'hasSupplierArchived';
};

export const getDueDate = (woPriority, currentDueDate) => {
	const expectedResolutionInSeconds = nullSafeGet('expectedResolutionInSeconds', woPriority);
	if (expectedResolutionInSeconds === null || expectedResolutionInSeconds === undefined) {
		if (currentDueDate) {
			return currentDueDate;
		} else {
			return moment();
		}
	} else {
		return moment(new Date(new Date().getTime() + expectedResolutionInSeconds * 1000));
	}
};

export const onCancelRequest = (props, state) => () => {
	const { history } = props;
	const { backLinkTo } = state;
	history.push(backLinkTo);
};

export const isAValidAndParitalWorkOrder = (workOrder: any): boolean => {
	if (!workOrder || !workOrder.id) {
		return false;
	}
	return workOrder.searchRecord === true;
};

export const isNoteValid = (note: any): boolean => {
	if (!note) {
		return false;
	}
	const hasText = note.text && note.text.length > 0;
	const hasPhoto = note.photo && note.photo.length > 0;
	const hasVideo = note.video && note.video.length > 0;
	const hasAudio = note.audio && note.audio.length > 0;
	const hasChangedBy = note.changedBy;

	return hasText || hasPhoto || hasVideo || hasAudio || hasChangedBy;
};

export const getWorkOrderLink = ({
	userType,
	tab,
	workOrder,
	targetCollectionName,
	location,
	BACK_TEXT,
}) => {
	const path = `/${userType}/workOrders/detail/${nullSafeGet('id', workOrder)}/${tab}${
		targetCollectionName ? `?targetCollectionName=${targetCollectionName}` : ''
	}`;
	return getLinkWIthBackLinkParams(location, BACK_TEXT, path, {
		preserve: true,
	});
};

export const getTaskListSummaryIfExist = (state) => {
	return state.includeWalkthroughTaskList && state.taskAnswer
		? [
				{
					key: `Task List (${nullSafeGetOrElse('taskAnswer.taskListAnswer', state, []).length})`,
					lineOneRender: (
						<div className="mb-3">
							<WalkthroughTaskListAnswerDisplay
								taskAnswer={state.taskAnswer}
								includeOnlyPendingTaskItems
								hideDisabledHelper
							/>
						</div>
					),
				},
		  ]
		: [];
};
